import usePagination from "@/hooks/pagination";
import { useEffect, useState } from "react";
import {
  addModuleToClient,
  createClient,
  deleteClient,
  generateEnrollmentToken,
  getClients,
  updateClient,
  type Client,
} from "@/api/client.service";
import { CrudPage } from "@/components/crud/CrudPage";
import { ClientsForm } from "./ClientsForm";
import { useNavigate } from "react-router";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import { Add, Delete, Edit, Business, Key, MonitorHeart } from "@mui/icons-material"
import { FilterModal } from "@/components/FilterModal";
import { EnrollmentTokenDialog } from "@/components/EnrollmentTokenDialog";
import { useNotification } from "@/context/NotificationContext";
import { useLogger } from "@/hooks/useLogger";
import { LogEventType } from "@/api/log.service";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { getSystems } from "@/api/supportedSystem.service";

type ClientFilter = {
  name?: string;
  email?: string;
  groupName?: string;
  system?: string;
  active?: boolean;
};

export const ClientsPage = () => {
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { notify } = useNotification();
  const { log } = useLogger();
  const navigate = useNavigate();

  // Filters
  const [inputValues, setInputValues] = useState({ name: "", email: "", groupName: "", system: "" });
  const [filters, setFilters] = useState<ClientFilter>({});

  // Composition Hooks
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Client>();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Enrollment token dialog
  const [tokenDialog, setTokenDialog] = useState<{ open: boolean; token: string; clientId: string; clientName: string }>({
    open: false, token: "", clientId: "", clientName: "",
  });

  // Data Fetching
  const [systems, setSystems] = useState<{ label: string, id: string }[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadClients = async () => {
    // setIsLoading(true);
    try {
      const data = await getClients({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...filters
      });
      setTotal(data.total ?? 0);
      setClients(data.clients ?? []);
    } catch (error) {
      notify("Falha ao carregar clientes", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof inputValues, value: string) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setFilters({
      name: inputValues.name || undefined,
      email: inputValues.email || undefined,
      groupName: inputValues.groupName || undefined,
      system: inputValues.system || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    const empty = { name: "", email: "", groupName: "", system: "" };
    setInputValues(empty);
    setFilters({});
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  useEffect(() => { loadClients(); }, [page, rowsPerPage, filters]);

  useEffect(() => {
    const loadSystems = async () => {
      const returnedSystems = await getSystems();
      setSystems(returnedSystems.map(s => ({ label: s, id: s })));
    }

    loadSystems();
  }, []);

  // 3. Action Handlers
  const onSave = async (values: Partial<Client> & { modules?: string[] }) => {
    // Validate required fields
    if (!values.name?.trim()) {
      notify("Nome é obrigatório", "error");
      return;
    }
    if (!values.email?.trim()) {
      notify("Email é obrigatório", "error");
      return;
    }
    if (!values.system_id) {
      notify("Sistema é obrigatório", "error");
      return;
    }
    if (values.params !== undefined && values.params !== null) {
      if (typeof values.params !== "object" || Array.isArray(values.params)) {
        notify("O campo Parâmetros deve ser um objeto JSON válido", "error");
        return;
      }
    }

    try {
      if (editing) {
        const result = await updateClient(editing.id, values);
        log(LogEventType.Updated, result, "client", result.id, "info");
        notify("Cliente atualizado!", "success");
      } else {
        const response = await createClient(values as Client);
        const result = response.client;
        if (values.modules?.length) {
          await Promise.all(
            (values.modules as string[]).map(moduleId =>
              addModuleToClient({ client_id: result.id, module_id: moduleId })
            )
          );
        }
        log(LogEventType.Created, result, "client", result.id, "info");
        notify("Cliente criado!", "success");

        if (response.enrollment_token) {
          setTokenDialog({
            open: true,
            token: response.enrollment_token,
            clientId: result.id,
            clientName: result.name,
          });
        }
      }
      loadClients();
      handleClose();
    } catch (e) {
      notify("Erro ao salvar cliente", "error");
    }
  };

  const onDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      const client = clients.find(c => c.id === deleteId);
      await deleteClient(deleteId);
      log(LogEventType.Deleted, client ?? { id: deleteId }, "client", deleteId, "info");
      notify("Cliente deletado!", "success");
      loadClients();
    } finally {
      setDeleteId(null);
    }
  };

  const handleRegenerateToken = async (clientId: string, clientName: string) => {
    try {
      const { token } = await generateEnrollmentToken(clientId);
      setTokenDialog({ open: true, token, clientId, clientName });
    } catch {
      notify("Erro ao gerar token de inscrição", "error");
    }
  };

  if (isLoading) return <div>Carregando clientes...</div>;

  return (
    <CrudPage
      title="Clientes"
      subtitle="Gerencie os clientes do sistema"
      headerActions={
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Novo Cliente
        </Button>
      }
    >
      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Nome"
          size="small"
          fullWidth
          value={inputValues.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
        />
        <TextField
          label="Email"
          size="small"
          fullWidth
          value={inputValues.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
        />
        <TextField
          label="Grupo"
          size="small"
          fullWidth
          value={inputValues.groupName}
          onChange={(e) => handleInputChange("groupName", e.target.value)}
        />
        <Autocomplete
          options={systems}
          size="small"
          fullWidth
          value={systems.find((s) => s.id === inputValues.system) || null}
          onChange={(_, newValue) => {
            handleInputChange("system", newValue ? newValue.id : "");
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Sistema" variant="outlined" />
          )}
        />
      </FilterModal>
      <DataTable
        data={clients}
        columns={[
          { key: "name", label: "Nome" },
          { key: "group_name", label: "Nome de grupo" },
          { key: "email", label: "Email" },
          { key: "system_id", label: "Sistema" },
          {
            key: "heartbeat",
            label: "Status",
            render: (client: Client) => {
              const hb = client.heartbeat;
              const online = !!hb?.online;
              const healthy = online && hb?.status !== "error";
              const color = !online ? "grey.400" : healthy ? "success.main" : "error.main";
              const label = !online
                ? "Offline"
                : `Online${hb?.message ? ` — ${hb.message}` : ""} (desde ${new Date(hb!.last_seen_at).toLocaleString()})`;
              return (
                <Tooltip title={label}>
                  <Box
                    sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, display: "inline-block" }}
                  />
                </Tooltip>
              );
            },
          },
          {
            key: "migration",
            label: "Migração",
            render: (client: Client) => {
              const mig = client.heartbeat?.migration_state;
              if (!mig) return <>—</>;
              const label =
                mig === "running"
                  ? "Aplicando migração..."
                  : mig === "failed"
                    ? `Falhou${client.heartbeat?.migration_error ? `: ${client.heartbeat.migration_error}` : ""}`
                    : `v${client.heartbeat?.migration_version ?? 0}`;
              const color = mig === "failed" ? "error.main" : mig === "running" ? "warning.main" : "text.secondary";
              return (
                <Tooltip title={label}>
                  <Box component="span" sx={{ color, fontSize: "0.8rem" }}>
                    {label}
                  </Box>
                </Tooltip>
              );
            },
          },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(client) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Gerar Token de Inscrição">
              <IconButton onClick={() => handleRegenerateToken(client.id, client.name)} color="info">
                <Key />
              </IconButton>
            </Tooltip>
            <Tooltip title="Visualizar Empresas">
              <IconButton onClick={() => navigate(`/empresas/${client.id}`)} color="secondary">
                <Business />
              </IconButton>
            </Tooltip>
            <Tooltip title="Status">
              <IconButton onClick={() => navigate(`/clientes/${client.id}/agendamentos`)} color="secondary">
                <MonitorHeart />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => handleEdit(client)} color="primary">
              <Edit />
            </IconButton>
            <IconButton onClick={() => setDeleteId(client.id)} color="error">
              <Delete />
            </IconButton>
          </Stack>
        )}
      />

      <CrudModal
        open={open}
        onClose={handleClose}
        title={editing ? "Editar Cliente" : "Novo Cliente"}
        initialData={editing}
        extraFormProps={{ systems }}
        onSubmit={onSave}
        Form={ClientsForm}
      />

      <ConfirmModal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={onDeleteConfirm}
        title="Exclusão de cliente"
        btnText="Confirmar"
        text={`Tem certeza que deseja excluir ${clients.find(c => c.id === deleteId)?.name}?`}
      />

      <EnrollmentTokenDialog
        open={tokenDialog.open}
        onClose={() => setTokenDialog(prev => ({ ...prev, open: false }))}
        token={tokenDialog.token}
        clientName={tokenDialog.clientName}
        onRegenerate={async () => {
          const { token } = await generateEnrollmentToken(tokenDialog.clientId);
          setTokenDialog(prev => ({ ...prev, token }));
        }}
      />
    </CrudPage>
  );
};